/**
 * Shared header for the ELF-CIRCLE inner pages.
 *
 * The design draws this nav slightly differently on each artboard (some boards
 * list Events or AI & GEO); the site standardises on the four real pages so
 * every link goes somewhere. `onFilm` switches to the light-on-photography
 * treatment used over the Who We Are hero band.
 *
 * The logo is a bundled asset, not a CMS slot — the inner pages are static.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import ElfModeToggle from './ElfModeToggle';
import ElfNavMenu from './ElfNavMenu';
import { useConsultOpener } from './elfConsult';
import { useAuth } from '../../../auth';
import { isAdmin } from '../../../../shared/utils';
import elfLogo from '../../../../assets/elf/elfcircle-logo.png';
import { route } from '../../../../shared/utils';

export interface NavItem {
  label: string;
  to: string;
}

export const ELF_NAV: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'Who We Are', to: '/who-we-are' },
  { label: 'Our Story', to: '/our-story' },
  { label: 'The Trifecta', to: '/the-trifecta' },
  { label: 'Services', to: '/services' },
  { label: 'AI & GEO', to: '/ai-and-geo' },
  { label: 'Portfolio', to: '/portfolio' },
  { label: 'Markets', to: '/launch-markets' },
  { label: 'Ecosystem', to: '/ecosystem' },
  { label: 'Why ELF-CIRCLE', to: '/why-elf-circle' },
  { label: 'Events', to: '/networking-events' },
  { label: 'Made In New Jersey', to: '/made-in-new-jersey' },
  { label: 'Sponsorship', to: '/sponsorship' },
  { label: 'Cultural Intelligence', to: '/cultural-intelligence' },
  { label: 'Recognition', to: '/recognition' },
  { label: 'Team & Careers', to: '/team' },
  { label: 'Shop', to: '/shop' },
];

/**
 * Entries that move under `More`.
 *
 * The header shell is capped at 1536px, leaving 1336px for links against the
 * ~1514px all eleven need — so the row wrapped at every viewport, and a wider
 * monitor never helped. Keeping the seven highest-traffic entries inline puts
 * the row back on one line with room to spare, and gives the admin-only
 * `Enquiries` link somewhere to land instead of overflowing on its own.
 *
 * Everything added after the first eleven lands here by default: the inline
 * row is full at seven, so a new page joins `More` rather than re-wrapping
 * the header. The dropdown scrolls once it outgrows the viewport.
 */
const MORE_PATHS = new Set([
  '/the-trifecta',
  '/launch-markets',
  '/ecosystem',
  '/why-elf-circle',
  '/made-in-new-jersey',
  '/sponsorship',
  '/cultural-intelligence',
  '/recognition',
  '/team',
  '/shop',
]);

/** Rendered inline in the header. Derived so both lists stay in ELF_NAV order. */
export const ELF_NAV_PRIMARY: NavItem[] = ELF_NAV.filter((i) => !MORE_PATHS.has(i.to));

/** Rendered inside the `More` dropdown. */
export const ELF_NAV_MORE: NavItem[] = ELF_NAV.filter((i) => MORE_PATHS.has(i.to));

/**
 * Inline entries that survive on a laptop-width window (1081–1360px). The
 * rest of the primary row folds into `More` there; below 1080px the burger
 * takes over entirely. See the matching rules in elf-pages.css.
 */
const CORE_PATHS = new Set(['/', '/services', '/portfolio', '/networking-events']);
export const isCoreNav = (item: NavItem): boolean => CORE_PATHS.has(item.to);

/** `More` for the laptop-width row: the folded primary entries first. */
export const ELF_NAV_MORE_COMPACT: NavItem[] = [
  ...ELF_NAV_PRIMARY.filter((i) => !isCoreNav(i)),
  ...ELF_NAV_MORE,
];

/** Class for an inline link; non-core ones hide on a laptop-width window. */
export const navLinkClass = (item: NavItem, current?: string, extra = ''): string =>
  [
    'cine-nav',
    extra,
    isCoreNav(item) ? '' : 'cine-nav--extra',
    item.to === current ? 'here' : '',
  ].filter(Boolean).join(' ');

export interface ElfPageNavProps {
  /** Path of the page being rendered, so its link shows the `here` state. */
  current?: string;
  /** Light treatment for navs sitting over a photograph. */
  onFilm?: boolean;
}

export const ElfPageNav: React.FC<ElfPageNavProps> = ({ current, onFilm }) => {
  const bookACall = useConsultOpener({ source: 'nav', title: 'Book a call' });
  const { user, token } = useAuth();
  // Admin-only shortcut to the captured consultation requests.
  const showEnquiries = Boolean(token) && isAdmin(user);
  // Same entries the inline row renders, for the narrow-viewport menu.
  const menuItems: NavItem[] = showEnquiries
    ? [...ELF_NAV, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV;
  // The admin shortcut rides in `More` rather than inline — it is the entry
  // that pushed the row over the edge for exactly the people who use it most.
  const moreItems: NavItem[] = showEnquiries
    ? [...ELF_NAV_MORE, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV_MORE;
  const compactMoreItems: NavItem[] = showEnquiries
    ? [...ELF_NAV_MORE_COMPACT, { label: 'Enquiries', to: '/enquiries' }]
    : ELF_NAV_MORE_COMPACT;
  return (
  <div className={onFilm ? 'wnav wnav-onfilm' : 'wnav'}>
    <div className="wnav__inner">
      <Link to={route('/')} aria-label="ELF-CIRCLE home">
        <img className="wnav__logo" src={elfLogo} alt="ELF-CIRCLE" />
      </Link>
      <div className="wnav__links">
        {ELF_NAV_PRIMARY.map((item) => (
          <Link
            key={item.to}
            to={route(item.to)}
            className={navLinkClass(item, current)}
          >
            {item.label}
          </Link>
        ))}
        <span className="elf-navmore--wide">
          <ElfNavMenu variant="more" items={moreItems} current={current} />
        </span>
        <span className="elf-navmore--compact">
          <ElfNavMenu variant="more" items={compactMoreItems} current={current} />
        </span>
        <ElfModeToggle />
        <button type="button" className="cta mag wnav__cta" onClick={bookACall}>
          Book a call
        </button>
        <ElfNavMenu items={menuItems} current={current} />
      </div>
      </div>
    </div>
  );
};

/** Breadcrumb trail. The last entry renders as the current page. */
export const ElfCrumb: React.FC<{ trail: (NavItem | string)[] }> = ({ trail }) => (
  <div className="crumb">
    {trail.map((entry, i) => {
      const last = i === trail.length - 1;
      return (
        <React.Fragment key={typeof entry === 'string' ? entry : entry.to}>
          {i > 0 && <span>→</span>}
          {last || typeof entry === 'string' ? (
            <b>{typeof entry === 'string' ? entry : entry.label}</b>
          ) : (
            <Link to={route(entry.to)}>{entry.label}</Link>
          )}
        </React.Fragment>
      );
    })}
  </div>
);

export default ElfPageNav;
